// kontaktLoeschen.ts — „Kontakt löschen" (Eimer) aus den Beziehungslisten der
// Akte (Plan 2026-10-01 Verbindungen/Beziehungen, Paket A2, User 02.10.2026).
//
// Zwei Aktionen je Zeile, bewusst getrennt:
//   - X = Entfernen: nimmt nur die Verbindung aus der Liste, wirkt wie bisher
//     erst beim Speichern (persistRelations).
//   - Eimer = Kontakt löschen: löscht den GEGENKONTAKT selbst (Soft-Delete,
//     Papierkorb) — sofort, mit Pflicht-Grund im eigenen Popup, danach lädt
//     die Akte neu (gelöschte Gegenkontakte fallen beim Laden heraus).
//
// Den Eimer gibt es nur in der Akte (AkteSofortSpeichernContext), nur für
// gespeicherte Gegenkontakte und nur mit Löschrecht (admin/gfgl oder die
// temporäre Lösch-Allowlist, wie die Kontaktliste). In der Neuanlage bleibt
// es beim X.
//
// Hat das Formular ungespeicherte Änderungen, wird NICHT gelöscht: Das
// Neuladen danach würfe sie weg (Muster akteSofortSpeichern.ts).

import * as React from "react";
import { apiFetch } from "../../utils/apiFetch";
import { eingabe, hinweisen } from "../redesign/bestaetigen";
import { useAkteSofortSpeichern, type AkteSofortSpeichern } from "./akteSofortSpeichern";

export type KontaktZumLoeschen = { contactId: number; name: string; typ?: "Person" | "Firma" };

/** Hinweis, wenn wegen offener Formularänderungen nicht gelöscht wird. */
export const LOESCHEN_UNGESPEICHERT_HINWEIS =
  "In der Akte gibt es noch ungespeicherte Änderungen. Bitte zuerst speichern (Schloss schließen) oder verwerfen — danach lässt sich der Kontakt löschen.";

export type KontaktLoeschenDeps = {
  sofort: Pick<AkteSofortSpeichern, "hatUngespeicherte" | "neuLaden">;
  frageGrund: typeof eingabe;
  hinweis: typeof hinweisen;
  loeschen: (contactId: number, grund: string) => Promise<unknown>;
};

/** Grund für das Löschen ist Pflicht. */
export const grundPruefen = (wert: string): string | null =>
  wert.trim() ? null : "Bitte einen Grund für das Löschen angeben.";

/**
 * Der ganze Ablauf, ohne React — testbar mit eingesetzten Abhängigkeiten.
 * Ergebnis: `true` = gelöscht (und neu geladen), sonst `false`.
 */
export async function kontaktLoeschenAblauf(kontakt: KontaktZumLoeschen, deps: KontaktLoeschenDeps): Promise<boolean> {
  if (deps.sofort.hatUngespeicherte()) {
    await deps.hinweis({ titel: "Erst speichern", text: LOESCHEN_UNGESPEICHERT_HINWEIS });
    return false;
  }
  const name = kontakt.name.trim() || (kontakt.typ === "Firma" ? "Diese Firma" : "Dieser Kontakt");
  const grund = await deps.frageGrund({
    titel: "Kontakt löschen",
    text:
      `„${name}“ wird als Kontakt gelöscht und landet im Papierkorb — nicht nur die Verbindung zu dieser Akte.\n` +
      "Wer nur die Verbindung lösen möchte, nimmt stattdessen das X (Entfernen).",
    label: "Grund",
    platzhalter: "z. B. doppelt angelegt",
    ja: "Kontakt löschen",
    ton: "danger",
    pruefen: grundPruefen,
  });
  if (grund == null) return false;
  try {
    await deps.loeschen(kontakt.contactId, grund.trim());
  } catch (e) {
    const status = (e as { status?: number } | null)?.status;
    await deps.hinweis({
      titel: "Kontakt nicht gelöscht",
      text:
        status === 403
          ? "Für diesen Kontakt fehlt das Recht zum Löschen."
          : status === 404
            ? "Den Kontakt gibt es nicht mehr — die Akte wird neu geladen."
            : "Der Kontakt konnte nicht gelöscht werden. Bitte später erneut versuchen.",
      ton: "danger",
    });
    if (status === 404) deps.sofort.neuLaden();
    return false;
  }
  deps.sofort.neuLaden();
  return true;
}

/** Soft-Delete wie in der Kontaktliste: `DELETE /contact/:id?reason=…`. */
export const kontaktLoeschenApi = (contactId: number, grund: string) =>
  apiFetch<void>(`contact/${contactId}`, { method: "DELETE", params: { reason: grund } });

/**
 * Liefert für einen Gegenkontakt den Klick-Handler des Eimers — oder
 * `undefined`, wenn es den Eimer hier nicht gibt (Neuanlage, kein gespeicherter
 * Gegenkontakt, kein Löschrecht).
 */
export function useKontaktLoeschen(): (kontakt: { contactId?: number; name: string; typ?: "Person" | "Firma" } | undefined) =>
  | (() => void)
  | undefined {
  const sofort = useAkteSofortSpeichern();
  return React.useCallback(
    (kontakt) => {
      if (!sofort?.darfKontakteLoeschen || !kontakt?.contactId) return undefined;
      const k: KontaktZumLoeschen = { contactId: kontakt.contactId, name: kontakt.name, typ: kontakt.typ };
      return () => {
        void kontaktLoeschenAblauf(k, {
          sofort,
          frageGrund: eingabe,
          hinweis: hinweisen,
          loeschen: kontaktLoeschenApi,
        });
      };
    },
    [sofort],
  );
}
